import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  InputNumber,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { hrqGetRange, hrqSaveMany } from "./harvestRequirement.storage.js";
import { BACKEND_DATE_FORMAT } from "../../../consts/index.js";
import { UserContext } from "../../../context/UserContext.jsx";

/** @typedef {import("./harvestOptimizer.types.d.ts").HarvestRequirementProps} HarvestRequirementProps */

const { Text } = Typography;

const addOrdinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};
const dayHeading = (d) =>
  `${d.format("dddd")} (${addOrdinal(d.date())} ${d.format("MMM")})`;

const weekMonToSun = (anchor) => {
  const dow = anchor.day();
  const monday = anchor.subtract((dow + 6) % 7, "day").startOf("day");
  return Array.from({ length: 7 }, (_, i) => monday.add(i, "day"));
};

export default function HarvestRequirement({
  messageApi,
  currentOrg,
  currentSite,
  currentCrop,
  siteMetadata,
  greenHouseLabel,
  blockLabel,
}) {
  const visibleDays = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  const measurementTypes = [
    { name: "Rows", value: "row" },
    { name: "Total Yield (Kg)", value: "total_yield" },
    { name: "Number of Pallets", value: "pallet" },
    { name: "Number of Crates", value: "crate" },
  ];

  const [defaultMeasurementType, setDefaultMeasurementType] = useState("row");
  const [anchorDate, setAnchorDate] = useState(dayjs().startOf("day"));
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState("");
  // countsByIso: { [iso]: { value: number, type: string } }
  const [countsByIso, setCountsByIso] = useState({});

  const { userData } = useContext(UserContext);
  // const canEdit = !!userData?.is_superuser;
  const canEdit = true;

  const orgId =
    typeof currentOrg === "string" ? currentOrg : currentOrg?.id || "org";
  const siteId =
    typeof currentSite === "string" ? currentSite : currentSite?.id || "site";
  const cropId =
    typeof currentCrop === "string" ? currentCrop : currentCrop?.id || "crop";

  const filterToVisibleDays = (dates) => {
    const want = new Set((visibleDays || []).map((x) => x.toLowerCase()));
    return dates.filter((d) => want.has(d.format("dddd").toLowerCase()));
  };

  const weekDays = useMemo(
    () => filterToVisibleDays(weekMonToSun(anchorDate)),
    [anchorDate, visibleDays],
  );

  const startISO = weekDays[0]?.format(BACKEND_DATE_FORMAT);
  const endISO = weekDays[weekDays.length - 1]?.format(BACKEND_DATE_FORMAT);

  const typeOptions = useMemo(
    () => measurementTypes.map((t) => ({ label: t.name, value: t.value })),
    [measurementTypes],
  );

  const rows = useMemo(
    () =>
      weekDays.map((d) => {
        const iso = d.format(BACKEND_DATE_FORMAT);
        const entry = countsByIso[iso] || {
          value: 0,
          type: defaultMeasurementType,
        };
        return {
          key: iso,
          iso,
          label: dayHeading(d),
          value: entry.value,
          type: entry.type || defaultMeasurementType,
        };
      }),
    [weekDays, countsByIso, defaultMeasurementType],
  );

  useEffect(() => {
    const t = siteMetadata?.HARVEST_OPTIMIZER?.DEFAULT_UNIT_OF_MEASUREMENT;
    if (t && typeof t === "string") setDefaultMeasurementType(t);
  }, [siteMetadata]);

  useEffect(() => {
    if (!siteId || !startISO || !endISO) return;
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setErrorText("");
        const existing = await hrqGetRange(siteId, startISO, endISO);
        const merged = Object.fromEntries(
          weekDays.map((d) => {
            const iso = d.format(BACKEND_DATE_FORMAT);
            const e = existing[iso];
            return [
              iso,
              {
                value: e?.value ?? 0,
                type: e?.type ?? defaultMeasurementType,
              },
            ];
          }),
        );
        if (alive) setCountsByIso(merged);
      } catch (e) {
        if (alive) setErrorText(e?.message || "Failed to load requirements");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [siteId, startISO, endISO, defaultMeasurementType]);

  const columns = [
    {
      title: "Day",
      dataIndex: "label",
      key: "label",
      width: 260,
      render: (txt) => <Text>{txt}</Text>,
    },
    {
      title: "Requirement quantity",
      dataIndex: "value",
      key: "value",
      render: (_, record) => (
        <Space>
          <InputNumber
            min={0}
            value={record.value}
            disabled={!canEdit}
            onChange={(val) => {
              if (!canEdit) return;
              setCountsByIso((prev) => ({
                ...prev,
                [record.iso]: {
                  value: Number(val) || 0,
                  type: prev[record.iso]?.type || defaultMeasurementType,
                },
              }));
            }}
          />
          <Select
            style={{ minWidth: 180 }}
            options={typeOptions}
            value={record.type || defaultMeasurementType}
            disabled={!canEdit}
            onChange={(t) => {
              if (!canEdit) return;
              setCountsByIso((prev) => ({
                ...prev,
                [record.iso]: {
                  value: prev[record.iso]?.value ?? 0,
                  type: t,
                },
              }));
            }}
          />
        </Space>
      ),
    },
  ];

  const onSave = async () => {
    if (!canEdit) {
      messageApi?.warning("Read-only access. Ask an admin to update.");
      return;
    }
    try {
      setLoading(true);
      const payload = weekDays.map((d) => {
        const iso = d.format(BACKEND_DATE_FORMAT);
        const e = countsByIso[iso] || {
          value: 0,
          type: defaultMeasurementType,
        };
        return {
          orgId,
          siteId,
          cropId,
          date: iso,
          type: e.type || defaultMeasurementType,
          value: e.value ?? 0,
        };
      });
      const res = await hrqSaveMany(payload);
      if (res?.ok) messageApi?.success("Data Saved");
      else messageApi?.error("Save failed");
    } catch (e) {
      messageApi?.error(e?.message || "Save failed");
      console.log("Supabase Error", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card variant="outlined">
      <div className="flex items-center justify-between mb-3 gap-3">
        <Space size="middle" wrap>
          <Text strong>Week:</Text>
          <DatePicker
            picker="week"
            value={anchorDate}
            onChange={(d) => d && setAnchorDate(d.startOf("day"))}
            allowClear={false}
          />
        </Space>
        <Button
          type="primary"
          onClick={onSave}
          loading={loading}
          disabled={!canEdit}
        >
          Save
        </Button>
      </div>

      {errorText ? (
        <Alert type="error" message={errorText} showIcon className="mb-3" />
      ) : null}

      <Table
        columns={columns}
        dataSource={rows}
        pagination={false}
        loading={loading}
        size="small"
        bordered
      />
    </Card>
  );
}
